// src/theme/sacredCanvas.js
// "Sacred Canvas / Digital Medersa" design tokens — derived from Stitch
// DESIGN.md (Medersa Bou Inania). All colours are warm — no pure grey.

export const palette = {
  light: {
    primary: '#1F5F4A',
    primaryContainer: '#004734',
    primaryFixed: '#B0F0D4',
    onPrimary: '#FFFFFF',
    // Accent is now a brighter emerald (was gold). Keeps a visible
    // high-emphasis accent distinct from the deep-green primary, on-brand.
    secondary: '#2E9E6E',
    secondaryContainer: '#1F8A5C',
    secondaryFixed: '#BCEFD8',
    onSecondary: '#052818',
    tertiary: '#3FA9A0',
    tertiaryContainer: '#005F59',
    tertiaryFixed: '#8FF4E9',
    onTertiary: '#FFFFFF',
    // Neutral white surfaces (was warm cream) — green + white theme.
    surface: '#FFFFFF',
    surfaceBright: '#FFFFFF',
    surfaceDim: '#E7EFEA',
    surfaceContainerLowest: '#FFFFFF',
    surfaceContainerLow: '#F4F8F5',
    surfaceContainer: '#EEF4F0',
    surfaceContainerHigh: '#E7EFEA',
    surfaceContainerHighest: '#DFE9E3',
    surfaceVariant: '#DFE9E3',
    onSurface: '#10201A',
    onSurfaceVariant: '#3B4A43',
    ink: '#10201A',
    outline: '#6B7A72',
    outlineVariant: '#C2D0C8',
    error: '#BA1A1A',
    errorContainer: '#FFDAD6',
    onError: '#FFFFFF',
    success: '#1F5F4A',
    warning: '#E6A817',
    info: '#3FA9A0',
  },
  dark: {
    primary: '#5FBF8E',
    primaryContainer: '#0A513D',
    primaryFixed: '#B0F0D4',
    onPrimary: '#002116',
    // Accent is now mint-green (was gold) — black + green dark theme.
    secondary: '#57D6A0',
    secondaryContainer: '#0C5E43',
    secondaryFixed: '#BCEFD8',
    onSecondary: '#052818',
    tertiary: '#72D7CD',
    tertiaryContainer: '#00504B',
    tertiaryFixed: '#8FF4E9',
    onTertiary: '#00201D',
    // Cooler near-black green surfaces (was warm-tinted).
    surface: '#0C120F',
    surfaceBright: '#18211D',
    surfaceDim: '#080C0A',
    surfaceContainerLowest: '#050806',
    surfaceContainerLow: '#121A16',
    surfaceContainer: '#18211D',
    surfaceContainerHigh: '#1F2925',
    surfaceContainerHighest: '#293530',
    surfaceVariant: '#293530',
    onSurface: '#E6EFE8',
    onSurfaceVariant: '#BFCAC3',
    ink: '#E6EFE8',
    outline: '#869089',
    outlineVariant: '#3C4742',
    error: '#FFB4AB',
    errorContainer: '#93000A',
    onError: '#690005',
    success: '#5FBF8E',
    warning: '#F2C75C',
    info: '#72D7CD',
  },
};

export const fonts = {
  display: '"Aref Ruqaa", "Amiri", "Noto Naskh Arabic", serif',
  body: '"Noto Naskh Arabic", "Amiri", serif',
  latin: '"Newsreader", "Noto Serif", serif',
  label: '"Work Sans", "Noto Sans Arabic", sans-serif',
};

export const radii = {
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  full: 9999,
};

// 4-pt spacing ladder. Components that previously reached for ad-hoc
// `mb: 2` / `paddingBlock: 24` should consume `tokens.space[i]` instead.
// The named tones below name the four most common rhythm gaps so vertical
// stacks read as paragraphs of breath rather than arbitrary numbers.
export const space = [0, 4, 8, 12, 16, 20, 24, 32, 40, 48, 64, 80, 96];

export const spaceTone = {
  silent: space[1],   //  4 — adjacent line, no separation felt
  breath: space[3],   // 12 — within a card / between label and value
  section: space[6],  // 24 — between content sections inside a screen
  chapter: space[9],  // 48 — between major regions of a screen
};

// Elevation. The Sacred Canvas turns MUI elevation OFF and uses surface
// tier for hierarchy; these three rungs are reserved for the deliberate
// floats (button hover, sticky bars, modals).
export const elevation = {
  flat:   'none',
  lift:   `0 12px 32px -16px var(--sacred-primary-rgb-mix, rgba(31,95,74,0.33))`,
  float:  `0 12px 40px -20px rgba(42,31,23,0.12)`,
  modal:  `0 24px 64px -24px rgba(42,31,23,0.22)`,
};

// Motion. No bounce, no elastic, no layout-property animation.
// Components must respect prefers-reduced-motion: reduce.
export const motion = {
  duration: {
    instant:    80,
    quick:     160,
    calm:      240,
    deliberate: 420,
  },
  easing: {
    outQuart: 'cubic-bezier(0.25, 1, 0.5, 1)',
    outQuint: 'cubic-bezier(0.22, 1, 0.36, 1)',
    outExpo:  'cubic-bezier(0.16, 1, 0.30, 1)',
  },
};

// Semantic state tokens — chip / heatmap / form-state surfaces. Always
// reach for these instead of inlining ad-hoc reds/greens. `soft*` is
// the chip variant (low-saturation fill + bordered outline). Hex is
// retained for MUI compat; OKLCH equivalents are documented in
// DESIGN.md.
export const stateTokens = {
  light: {
    success: {
      main:       '#1F5F4A',
      bg:         'rgba(31,95,74,0.10)',
      fg:         '#FFFFFF',
      soft:       '#E6F4EA',
      softFg:     '#1B5E20',
      softBorder: '#2E7D32',
    },
    warning: {
      main:       '#E6A817',
      bg:         'rgba(230,168,23,0.14)',
      fg:         '#271900',
      soft:       '#FFF6E0',
      softFg:     '#5E4200',
      softBorder: '#E6A817',
    },
    error: {
      main:       '#BA1A1A',
      bg:         '#FFDAD6',
      fg:         '#FFFFFF',
      soft:       '#FDECEA',
      softFg:     '#8E1616',
      softBorder: '#D32F2F',
    },
    info: {
      main:       '#3FA9A0',
      bg:         'rgba(63,169,160,0.14)',
      fg:         '#FFFFFF',
      soft:       '#E0F4F2',
      softFg:     '#005F59',
      softBorder: '#3FA9A0',
    },
  },
  dark: {
    success: {
      main:       '#5FBF8E',
      bg:         'rgba(95,191,142,0.18)',
      fg:         '#002116',
      soft:       'rgba(95,191,142,0.10)',
      softFg:     '#5FBF8E',
      softBorder: '#5FBF8E',
    },
    warning: {
      main:       '#F2C75C',
      bg:         'rgba(242,199,92,0.18)',
      fg:         '#271900',
      soft:       'rgba(242,199,92,0.10)',
      softFg:     '#F2C75C',
      softBorder: '#F2C75C',
    },
    error: {
      main:       '#FFB4AB',
      bg:         'rgba(255,180,171,0.18)',
      fg:         '#690005',
      soft:       'rgba(255,180,171,0.10)',
      softFg:     '#FFB4AB',
      softBorder: '#FFB4AB',
    },
    info: {
      main:       '#72D7CD',
      bg:         'rgba(114,215,205,0.18)',
      fg:         '#00201D',
      soft:       'rgba(114,215,205,0.10)',
      softFg:     '#72D7CD',
      softBorder: '#72D7CD',
    },
  },
};

// Chart tokens — Recharts and any future chart primitive consumes
// these. `series` is the small palette for categorical fills (≤6
// categories). `warmCoolRamp` is the warm→cool gradient for ranked
// bars (e.g. "most common" rules).
export const chartTokens = {
  light: {
    tooltipBg: '#F8F3E8',
    tooltipFg: '#1F5F4A',
    grid:      'rgba(31,95,74,0.10)',
    axis:      '#707974',
    series: ['#1F5F4A', '#E6A817', '#3FA9A0', '#0A513D', '#FCBB2F', '#8FF4E9'],
    warmCoolRamp: [
      '#BA1A1A', '#D86A1A', '#E6A817', '#C9B400',
      '#88A523', '#3F9650', '#1F5F4A', '#1B7F8A',
      '#0A6CB0', '#3F61A9', '#5749A0', '#704396',
      '#707974',
    ],
  },
  dark: {
    tooltipBg: '#1A2521',
    tooltipFg: '#EFE6D6',
    grid:      'rgba(143,154,147,0.20)',
    axis:      '#8F9A93',
    series: ['#5FBF8E', '#F2C75C', '#72D7CD', '#B0F0D4', '#FCBB2F', '#8FF4E9'],
    warmCoolRamp: [
      '#FFB4AB', '#F0915F', '#F2C75C', '#E0CC58',
      '#B7C76A', '#7FCFA0', '#5FBF8E', '#5DB6BD',
      '#5C9FCC', '#7E94CC', '#9787C2', '#A87DB6',
      '#8F9A93',
    ],
  },
};

// Feedback tokens — used by inline notices and the new EmptyState
// primitive (S5) for non-form messaging tier.
export const feedbackTokens = {
  light: {
    positive:  { ring: '#1F5F4A', bg: 'rgba(31,95,74,0.10)' },
    attention: { ring: '#E6A817', bg: 'rgba(230,168,23,0.12)' },
    critical:  { ring: '#BA1A1A', bg: 'rgba(186,26,26,0.10)' },
  },
  dark: {
    positive:  { ring: '#5FBF8E', bg: 'rgba(95,191,142,0.16)' },
    attention: { ring: '#F2C75C', bg: 'rgba(242,199,92,0.16)' },
    critical:  { ring: '#FFB4AB', bg: 'rgba(255,180,171,0.16)' },
  },
};

// Brand-correct keyboard focus ring. Pair `outline` + `boxShadow` so the
// halo sits outside the outline. Hex equivalents in light/dark below come
// from oklch(0.55 0.10 161) / oklch(0.78 0.13 158) — kept as hex for MUI
// compatibility but documented in DESIGN.md as OKLCH.
export const focus = {
  light: {
    ring:    '#2F8B6E',  // emerald, slightly lighter than primary
    halo:    'rgba(31, 95, 74, 0.22)',
    outline: '2px solid #2F8B6E',
    boxShadow: '0 0 0 4px rgba(31, 95, 74, 0.22)',
  },
  dark: {
    ring:    '#7FD3A8',
    halo:    'rgba(95, 191, 142, 0.30)',
    outline: '2px solid #7FD3A8',
    boxShadow: '0 0 0 4px rgba(95, 191, 142, 0.30)',
  },
};

// Mastery chip palette — one place to change for every screen
export const masteryColours = {
  new: {
    bg: 'rgba(232,225,208,0.9)',
    fg: '#2A1F17',
    border: 'rgba(42,31,23,0.08)',
    label: 'جديد',
  },
  learning: {
    bg: '#FCBB2F',
    fg: '#271900',
    border: 'rgba(39,25,0,0.10)',
    label: 'قيد التعلم',
  },
  reviewing: {
    bg: '#8FF4E9',
    fg: '#00201D',
    border: 'rgba(0,32,29,0.10)',
    label: 'قيد المراجعة',
  },
  mastered: {
    bg: '#1F5F4A',
    fg: '#FFFFFF',
    border: '#B0F0D4',
    label: 'متقن',
  },
};

